import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, MenuItem, Alert } from '@mui/material';
import type { Vendor, CreateVendorInput } from '../../api/vendors.api';

interface Props {
  open: boolean;
  initial?: Vendor | null;
  onClose: () => void;
  onSubmit: (input: CreateVendorInput) => Promise<void>;
  error?: string | null;
}

const empty: CreateVendorInput = { vendorName: '', status: 'active' };

export function VendorFormDialog({ open, initial, onClose, onSubmit, error }: Props) {
  const isEdit = Boolean(initial);
  const [form, setForm] = useState<CreateVendorInput>(empty);

  useEffect(() => {
    setForm(
      initial
        ? {
            vendorName: initial.vendorName,
            gstNumber: initial.gstNumber ?? undefined,
            panNumber: initial.panNumber ?? undefined,
            bankAccountName: initial.bankAccountName ?? undefined,
            bankAccountNumber: initial.bankAccountNumber ?? undefined,
            bankIfsc: initial.bankIfsc ?? undefined,
            email: initial.email ?? undefined,
            mobile: initial.mobile ?? undefined,
            regionId: initial.regionId,
            serviceCategory: initial.serviceCategory ?? undefined,
            status: initial.status,
          }
        : empty,
    );
  }, [initial, open]);

  const set = <K extends keyof CreateVendorInput>(k: K, v: CreateVendorInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? `Edit Vendor — ${initial?.vendorCode}` : 'Create Vendor'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Vendor Name" value={form.vendorName} onChange={(e) => set('vendorName', e.target.value)} required fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField label="GST Number" value={form.gstNumber ?? ''} onChange={(e) => set('gstNumber', e.target.value.toUpperCase())} fullWidth />
            <TextField label="PAN" value={form.panNumber ?? ''} onChange={(e) => set('panNumber', e.target.value.toUpperCase())} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Service Category" value={form.serviceCategory ?? ''} onChange={(e) => set('serviceCategory', e.target.value)} fullWidth />
            <TextField label="Region ID" type="number" value={form.regionId ?? ''} onChange={(e) => set('regionId', e.target.value ? Number(e.target.value) : null)} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Email" type="email" value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} fullWidth />
            <TextField label="Mobile" value={form.mobile ?? ''} onChange={(e) => set('mobile', e.target.value)} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Bank A/C Name" value={form.bankAccountName ?? ''} onChange={(e) => set('bankAccountName', e.target.value)} fullWidth />
            <TextField label="Bank A/C No." value={form.bankAccountNumber ?? ''} onChange={(e) => set('bankAccountNumber', e.target.value)} fullWidth />
            <TextField label="IFSC" value={form.bankIfsc ?? ''} onChange={(e) => set('bankIfsc', e.target.value.toUpperCase())} fullWidth />
          </Stack>
          <TextField select label="Status" value={form.status} onChange={(e) => set('status', e.target.value as CreateVendorInput['status'])} fullWidth>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="blacklisted">Blacklisted</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(form)}>{isEdit ? 'Save' : 'Create'}</Button>
      </DialogActions>
    </Dialog>
  );
}
